import { useEffect, useState, useMemo, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Skeleton } from '../components/ui/skeleton';
import { EmptyState } from '../components/EmptyState';
import { BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from 'recharts';
import { completionRate, calculateStreaks } from '../lib/streaks';
import { toISODate, addDays, isScheduledOn } from '../lib/dates';

export default function Stats() {
  const { user } = useAuth();
  const [habits, setHabits] = useState([]);
  const [logsByHabit, setLogsByHabit] = useState({});
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const [{ data: h }, { data: l }] = await Promise.all([
      supabase.from('habits').select('*').is('archived_at', null),
      supabase.from('habit_logs').select('*').order('log_date'),
    ]);
    setHabits(h || []);
    const m = {};
    for (const log of l || []) { (m[log.habit_id] ||= []).push(log); }
    setLogsByHabit(m);
    setLoading(false);
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // Last 30-day daily completion %
  const daily = useMemo(() => {
    const out = [];
    let d = new Date(); d.setHours(0,0,0,0);
    d = addDays(d, -29);
    for (let i = 0; i < 30; i++) {
      let scheduled = 0, done = 0;
      for (const h of habits) {
        if (isScheduledOn(h, d)) {
          scheduled++;
          const log = (logsByHabit[h.id] || []).find((x) => x.log_date === toISODate(d));
          if (log && (log.status === 'done' || (h.type !== 'binary' && Number(log.value || 0) >= Number(h.target_value || 1)))) done++;
        }
      }
      const pct = scheduled === 0 ? 0 : Math.round((done / scheduled) * 100);
      out.push({ date: toISODate(d).slice(5), pct });
      d = addDays(d, 1);
    }
    return out;
  }, [habits, logsByHabit]);

  // Best day of week (avg completion %)
  const dayOfWeek = useMemo(() => {
    const dows = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const acc = dows.map((d) => ({ day: d, scheduled: 0, done: 0 }));
    for (const h of habits) {
      const logs = logsByHabit[h.id] || [];
      const m = new Map(logs.map((l) => [l.log_date, l]));
      let d = new Date(); d.setHours(0,0,0,0); d = addDays(d, -89);
      for (let i = 0; i < 90; i++) {
        if (isScheduledOn(h, d)) {
          const idx = d.getDay();
          acc[idx].scheduled++;
          const log = m.get(toISODate(d));
          if (log && (log.status === 'done' || (h.type !== 'binary' && Number(log.value || 0) >= Number(h.target_value || 1)))) acc[idx].done++;
        }
        d = addDays(d, 1);
      }
    }
    return acc.map((x) => ({ day: x.day, pct: x.scheduled === 0 ? 0 : Math.round((x.done / x.scheduled) * 100) }));
  }, [habits, logsByHabit]);

  const perHabit = useMemo(() => habits.map((h) => ({
    name: h.name,
    color: h.color,
    rate30: completionRate(h, logsByHabit[h.id] || [], 30),
    rate90: completionRate(h, logsByHabit[h.id] || [], 90),
    streak: calculateStreaks(h, logsByHabit[h.id] || []).current,
    totalDone: (logsByHabit[h.id] || []).filter((l) => l.status === 'done').length,
  })), [habits, logsByHabit]);

  if (loading) return <div className="space-y-4"><Skeleton className="h-32 w-full rounded-2xl" /><Skeleton className="h-72 w-full rounded-2xl" /></div>;
  if (habits.length === 0) return <EmptyState icon={BarChart3} title="No stats yet" description="Create a habit and log a few days to see your insights bloom here." testid="stats-empty" />;

  return (
    <div className="space-y-8" data-testid="stats-page">
      <div>
        <p className="uppercase tracking-[0.2em] text-xs text-muted-foreground font-medium">Insights</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Your patterns</h1>
        <p className="text-sm text-muted-foreground mt-2">Honest, simple numbers. No vanity metrics.</p>
      </div>

      <section className="rounded-2xl border border-border bg-card/30 p-5 sm:p-7" data-testid="stats-daily-chart">
        <h2 className="font-heading font-medium mb-4">Daily completion · last 30 days</h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <LineChart data={daily} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} tick={{ fontSize: 10 }} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} domain={[0, 100]} tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
              <Line type="monotone" dataKey="pct" stroke="hsl(var(--primary))" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="rounded-2xl border border-border bg-card/30 p-5 sm:p-7" data-testid="stats-dow-chart">
        <h2 className="font-heading font-medium mb-4">Best days of the week</h2>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <BarChart data={dayOfWeek}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="day" stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} domain={[0, 100]} />
              <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="pct" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section data-testid="stats-per-habit">
        <h2 className="font-heading font-medium mb-3">Per habit</h2>
        <div className="rounded-2xl border border-border bg-card/30 overflow-hidden divide-y divide-border">
          {perHabit.map((h, i) => (
            <div key={i} className="flex items-center gap-4 px-4 py-4" data-testid={`stats-habit-row-${i}`}>
              <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: h.color }} />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{h.name}</div>
                <div className="text-xs text-muted-foreground">{h.totalDone} total · current {h.streak}d</div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">30d</div>
                <div className="font-heading font-semibold">{h.rate30}%</div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">90d</div>
                <div className="font-heading font-semibold">{h.rate90}%</div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
